// The backend sends two kinds of time (tickets-infra/docs/time-handling-decision.md):
// - instants (createdAt, paidAt, expiresAt, pairedAt...): ISO-8601 with an offset ("2026-10-25T15:00:00Z"), shown in
//   the events' zone whatever the browser's zone;
// - event wall times (startDate, endDate): what the organizer typed in the events' zone, without an offset
//   ("2026-11-14T20:00:00"), shown exactly as stored.
// Never format a wall time with new Date(value): it would be read in the browser's zone.

export const EVENT_ZONE = 'America/Bogota';
const LOCALE = 'es-CO';
const OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/** 25 de oct de 2026, 10:00 a. m. */
export const DATE_TIME_SHORT: Intl.DateTimeFormatOptions = {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
};

/** 25/10/2026: compact, for tables */
export const DATE_NUMERIC: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** 10:00 a. m. */
export const TIME_SHORT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

const format = (date: Date, options: Intl.DateTimeFormatOptions, timeZone: string, fallback: string) =>
    Number.isNaN(date.getTime()) ? fallback : new Intl.DateTimeFormat(LOCALE, { ...options, timeZone }).format(date);

/** An instant ("…Z", or a Date of this browser), shown in the events' zone. */
export const formatInstant = (value: string | Date | null | undefined, options: Intl.DateTimeFormatOptions,
                              fallback = ''): string => {
    if (!value) return fallback;
    return format(typeof value === 'string' ? new Date(value) : value, options, EVENT_ZONE, fallback);
};

/** An event wall time (startDate, endDate), shown as the organizer typed it. */
export const formatEventDate = (value: string | null | undefined, options: Intl.DateTimeFormatOptions,
                                fallback = ''): string => {
    if (!value) return fallback;
    // Sent with an offset after all: it is an instant
    if (OFFSET.test(value)) return formatInstant(value, options, fallback);
    // Read as UTC and formatted in UTC, the fields come out unchanged
    return format(new Date(`${value}Z`), options, 'UTC', fallback);
};

/** Whether two event wall times fall on the same day. */
export const sameEventDay = (first: string, second: string) => first.substring(0, 10) === second.substring(0, 10);
